import classNames from 'classnames';
import { ReactNode } from 'react';
import { last } from 'utils/array';
import logging from 'utils/logging';
import { None } from 'utils/types';

let sanitize: (s: string | undefined | null) => string;
if (typeof window === 'undefined') {
  /*
  Parse HTML to make sure it's valid and will keep working when server rendering

  The issue that's being fixed here is <li> elements outside an <ul> / <ol>.
  Because <li> tags are self-closing, it's valid to write code like
  <ul><li>1<li>2</ul> which will get interpreted as <ul><li>1</li><li>2</li></ul>

  Now an issue happens if you nest a 'bare' <li> inside another <li>:

   <ul><li><div> { <li>1</li> } </div></li></ul>

  will get interpreted as

   <ul><li> { <div></div></li><li> …

  the <div> is closed because it needs to close the <li>, and now the rest of the
  content is suddenly outside the <div>.

  This issue will only appear when server rendering, because on the client
  dangerouslySetInnerHTML is set using node.innerHTML, which will correctly scope
  your dangerous HTML to a specific DOM node.

  TODO: Fix this on the server (PAN-360)
  */
  // Another hack is to use a node require instead of an import to avoid the
  // dependency being included in the client bundle.
  // eslint-disable-next-line @typescript-eslint/no-var-requires
  const sanitizeHtml = require('sanitize-html');
  // eslint-disable-next-line @typescript-eslint/no-var-requires
  const { Parser } = require('html-tokenizer');

  const parse = (html: string | undefined) => {
    if (!html) return '';
    const nodes: string[] = [];
    const stack: string[] = [];
    for (const token of Parser.parse(html.replace(/<\/br>/g, '<br>'))) {
      if (token.type === 'open') {
        if (token.name === 'li') {
          // Make sure <li> elements are inside an <ul> or <ol>
          const current = last(stack);
          if (current !== 'ol' && current !== 'ul') {
            stack.push('ul');
            nodes.push('<ul>');
          }
        }
        if (!token.selfClosing) stack.push(token.name);
        nodes.push(`<${token.name}`);
        const entries = Object.entries(token.attributes);
        if (entries.length > 0) {
          nodes.push(
            ` ${entries.map(([key, value]) => `${key}="${value}"`).join(' ')}`,
          );
        }
        nodes.push(`>`);
      } else if (token.type === 'text') {
        nodes.push(token.text);
      } else if (token.type === 'close' && !token.selfClosing) {
        let head = stack.pop();
        if (head !== token.name && head === 'ul') {
          nodes.push('</ul>');
          head = stack.pop();
        }
        if (head !== token.name) {
          logging.error({ message: 'missing closing tag' });
          return '';
        }
        nodes.push(`</${token.name}>`);
      }
    }
    for (const tag of stack) {
      nodes.push(`</${tag}>`);
    }
    return nodes.join('');
  };

  sanitize = (s = '') =>
    parse(
      sanitizeHtml(s, {
        allowedTags: false, // false = accept everything
        allowVulnerableTags: true,
        allowedAttributes: {
          '*': ['href', 'name', 'target', 'src', 'key', 'style', 'class'],
        },
      }),
    );
} else {
  sanitize = (s) => s ?? '';
}

export type ProseProps = {
  children?: ReactNode;
  className?: string;
  inline?: boolean;
  dangerouslySetInnerHTML?: { __html: string | None };
  withDefaultStyles?: boolean;
};

export function Prose({
  children,
  className,
  inline = false,
  dangerouslySetInnerHTML,
  withDefaultStyles = true,
}: ProseProps): JSX.Element | null {
  let html;
  if (dangerouslySetInnerHTML) {
    html = { __html: sanitize(dangerouslySetInnerHTML.__html) };
  }

  const Tag = inline ? 'span' : 'div';
  return (
    <Tag
      className={classNames(withDefaultStyles && 'rich-text', className)}
      dangerouslySetInnerHTML={html}
      data-testid="prose-content"
    >
      {children}
    </Tag>
  );
}
